import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@carrent/core'
import { adminApi, carsApi, kbApi } from '@carrent/api'
import { Icon } from '@carrent/ui'
import { ALL_NAV_ITEMS } from './navGroups'

// Быстрые переходы берутся из той же навигации, что и Sidebar:
// один источник правды вместо второго захардкоженного списка,
// который расходился с меню при каждом добавлении раздела.
const QUICK = ALL_NAV_ITEMS

export function CommandPalette({ open, onClose }) {
  const navigate = useNavigate()
  const { isAdmin } = useAuthStore()
  const [q, setQ] = useState('')
  const [results, setResults] = useState({ cars: [], users: [], articles: [] })
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) { setQ(''); setResults({ cars: [], users: [], articles: [] }); setTimeout(() => inputRef.current?.focus(), 50) }
  }, [open])

  // Поиск по машинам и водителям (используем существующий параметр q)
  useEffect(() => {
    if (!open || !q.trim() || !isAdmin()) { setResults({ cars: [], users: [], articles: [] }); return }
    setLoading(true)
    const t = setTimeout(async () => {
      try {
        // Статьи ищутся здесь же: искать регламент, уже открыв палитру,
        // естественнее, чем закрывать её и идти в раздел.
        const [carsR, usersR, kbR] = await Promise.all([
          carsApi.adminList({ q, per_page: 5 }).catch(() => ({ data: {} })),
          adminApi.listUsers({ q, per_page: 5 }).catch(() => ({ data: {} })),
          kbApi.search(q).catch(() => ({ data: {} })),
        ])
        setResults({
          cars:  carsR.data?.data?.items || [],
          users: usersR.data?.data?.items || [],
          articles: (kbR.data?.data || []).slice(0, 5),
        })
      } finally { setLoading(false) }
    }, 300)
    return () => clearTimeout(t)
  }, [q, open, isAdmin])

  const go = useCallback((to) => { onClose(); navigate(to) }, [navigate, onClose])

  if (!open) return null

  const showQuick = !q.trim()
  const quickFiltered = QUICK.filter(x => x.label.toLowerCase().includes(q.toLowerCase()))

  return (
    <div className="cmdk-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cmdk-box">
        <input ref={inputRef} className="cmdk-input"
          placeholder="Поиск: водитель, машина, статья, раздел…"
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') onClose() }} />

        <div className="cmdk-results">
          {showQuick && (
            <>
              <div className="cmdk-section">Разделы</div>
              {QUICK.map(x => (
                <button type="button" key={x.to} className="cmdk-item" onClick={() => go(x.to)}>
                  <Icon name={x.icon} size={16} />
                  {x.label}
                </button>
              ))}
            </>
          )}

          {!showQuick && (
            <>
              {quickFiltered.length > 0 && <div className="cmdk-section">Разделы</div>}
              {quickFiltered.map(x => (
                <button type="button" key={x.to} className="cmdk-item" onClick={() => go(x.to)}>
                  <Icon name={x.icon} size={16} />
                  {x.label}
                </button>
              ))}

              {results.cars.length > 0 && <div className="cmdk-section">Машины</div>}
              {results.cars.map(c => (
                <button type="button" key={'c' + c.id} className="cmdk-item" onClick={() => go(`/admin/cars/${c.id}`)}>
                  <Icon name="car" size={16} />
                  {c.brand} {c.model}
                  <span className="cmdk-item__meta">{c.plate}</span>
                </button>
              ))}

              {results.users.length > 0 && <div className="cmdk-section">Водители</div>}
              {results.users.map(u => (
                <button type="button" key={'u' + u.id} className="cmdk-item" onClick={() => go(`/admin/users/${u.id}`)}>
                  <Icon name="users" size={16} />
                  {u.full_name}
                  <span className="cmdk-item__meta">{u.phone}</span>
                </button>
              ))}

              {results.articles.length > 0 && <div className="cmdk-section">База знаний</div>}
              {results.articles.map(a => (
                <button type="button" key={'a' + a.page_id} className="cmdk-item"
                  onClick={() => go(`/admin/kb`)}>
                  <Icon name="file" size={16} />
                  {a.title}
                </button>
              ))}

              {!loading && quickFiltered.length === 0 &&
               results.cars.length === 0 && results.users.length === 0 &&
               results.articles.length === 0 && (
                <div className="cmdk-empty">Ничего не найдено</div>
              )}
              {loading && <div className="cmdk-empty">Поиск…</div>}
            </>
          )}
        </div>

        <div className="cmdk-hint">
          <span>↵ открыть</span><span>Esc закрыть</span>
        </div>
      </div>
    </div>
  )
}
